import { Controller } from "@hotwired/stimulus"
import { activeWarnings, remainingMs, shouldAutoSubmit } from "../helpers/raven_timer.js"
import { findStepElement } from "../helpers/dom.js"

// One matrix at a time. The deadline comes from the server.
export default class extends Controller {
  static targets = ["step", "form", "clock", "warning", "image", "submitButton"]
  static values = {
    total: { type: Number, default: 1 },
    initialStep: { type: Number, default: 1 },
    deadline: { type: String, default: "" },
    warnMs: { type: String, default: "" },
    autosaveUrl: { type: String, default: "" }
  }

  connect() {
    this.currentStep = this.initialStepValue > 0 ? this.initialStepValue : 1
    this.formSubmitted = false
    this.shownWarnings = new Set()
    this.render()
    this.tick()
    this.timer = window.setInterval(() => this.tick(), 250)
  }

  disconnect() {
    if (this.timer) window.clearInterval(this.timer)
  }

  tick() {
    const deadline = Date.parse(this.deadlineValue)
    if (Number.isNaN(deadline)) return
    const remaining = remainingMs(Date.now(), deadline)
    if (this.hasClockTarget) this.clockTarget.textContent = formatRemaining(remaining)
    const thresholds = this.warnMsValue.split(",").map((item) => Number(item)).filter((item) => item > 0)
    activeWarnings(remaining, thresholds).forEach((ms) => {
      if (this.shownWarnings.has(ms) || !this.hasWarningTarget) return
      this.shownWarnings.add(ms)
      this.warningTarget.classList.remove("is-hidden")
      this.warningTarget.textContent = this.warningTarget.dataset.template || formatRemaining(ms)
    })
    if (shouldAutoSubmit(remaining)) this.finish()
  }

  answerChanged(event) {
    const step = event.target.closest("[data-step]")
    this.persist(step)
  }

  next(event) {
    event?.preventDefault()
    if (this.currentStep >= this.totalValue) return
    this.currentStep += 1
    this.render()
  }

  back(event) {
    event?.preventDefault()
    if (this.currentStep <= 1) return
    this.currentStep -= 1
    this.render()
  }

  zoom(event) {
    event.preventDefault()
    const step = findStepElement(this.stepTargets, this.currentStep)
    step?.querySelectorAll("[data-timed-matrix-target='image']").forEach((image) => {
      image.classList.toggle("raven-matrix__image--zoomed")
    })
  }

  onSubmit(event) {
    if (this.formSubmitted) {
      event.preventDefault()
      return
    }
    this.formSubmitted = true
  }

  finish() {
    if (this.formSubmitted || !this.hasFormTarget) return
    this.formSubmitted = true
    this.formTarget.requestSubmit()
  }

  render() {
    this.stepTargets.forEach((step) => {
      const open = Number(step.dataset.step) === this.currentStep
      step.hidden = !open
    })
  }

  persist(step) {
    if (!step || !this.hasAutosaveUrlValue || !this.autosaveUrlValue) return
    const selected = step.querySelector("input[type='radio']:checked")
    if (!selected) return
    const token = document.querySelector("meta[name='csrf-token']")?.content
    const body = new URLSearchParams()
    body.set("question_number", step.dataset.questionNumber)
    body.set("value", selected.value)
    fetch(this.autosaveUrlValue, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": token || "",
        "Accept": "application/json",
        "Content-Type": "application/x-www-form-urlencoded"
      },
      body
    }).catch(() => {})
  }
}

function formatRemaining(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000))
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${minutes}:${String(seconds).padStart(2, "0")}`
}
